import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Icon from "./Icon";

const languages = [
  { code: "nl", label: "Nederlands", flag: "🇳🇱" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
];

/**
 * LanguageSwitcher — a custom language selector with a bottom-sheet style modal.
 *
 * Props:
 *  - setLanguage: (code: string) => void — callback from App to persist the language
 *  - variant: "header" | "sidebar" — controls how the trigger button looks
 */
export default function LanguageSwitcher({ setLanguage, variant = "header" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[1];

  const handleSelect = (code) => {
    setLanguage(code);
    i18n.changeLanguage(code);
    localStorage.setItem("language", code);
    setOpen(false);
  };

  // Close on click outside (for the overlay, but also as safety net)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      {/* ── Trigger button ── */}
      {variant === "header" ? (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-[4px] text-neutral-100 active:scale-95 transition-transform"
          aria-label="Change language"
        >
          <span className="text-[16px] leading-none">{currentLang.flag}</span>
          <Icon
            icon="heroicons:chevron-down-20-solid"
            className="w-[14px] h-[14px] text-neutral-400"
          />
        </button>
      ) : (
        /* sidebar variant — wider, looks like the other sidebar items */
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-[8px] hover:bg-[#D0ADFB1A] p-[8px] rounded-[4px] w-full text-left font-normal text-[16px] leading-[22px] text-neutral-300 transition-all active:scale-[0.98]"
        >
          <span className="text-[20px] leading-none w-[25px] text-center">{currentLang.flag}</span>
          <span>{currentLang.label}</span>
          <Icon
            icon="heroicons:chevron-down-20-solid"
            className="w-[16px] h-[16px] text-neutral-400 ml-auto"
          />
        </button>
      )}

      {/* ── Bottom-sheet modal ── */}
      <div
        className={`fixed inset-0 z-[10000000] flex items-end justify-center transition-all duration-300 ${
          open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />

        {/* Sheet */}
        <div
          ref={panelRef}
          className={`relative w-full max-w-[420px] mx-auto bg-[#1A1226] border border-[#2E2740] rounded-t-[20px] px-[20px] pt-[12px] pb-[32px] transform transition-transform duration-300 ease-out ${
            open ? "translate-y-0" : "translate-y-full"
          }`}
        >
          {/* Drag handle */}
          <div className="flex justify-center mb-[16px]">
            <div className="w-[40px] h-[4px] rounded-full bg-[#3D3550]" />
          </div>

          {/* Title */}
          <p className="text-white font-semibold text-[18px] mb-[16px]">
            Choose language
          </p>

          {/* Options */}
          <div className="flex flex-col gap-[4px]">
            {languages.map((lang) => {
              const isActive = lang.code === currentLang.code;
              return (
                <button
                  key={lang.code}
                  onClick={() => handleSelect(lang.code)}
                  className={`flex items-center gap-[12px] px-[14px] py-[14px] rounded-[12px] transition-all duration-200 active:scale-[0.98] ${
                    isActive
                      ? "bg-[#AD7AEF]/15 border border-[#AD7AEF]/40"
                      : "bg-transparent border border-transparent hover:bg-[#D0ADFB10]"
                  }`}
                >
                  <span className="text-[24px] leading-none">{lang.flag}</span>
                  <span
                    className={`text-[16px] font-medium ${
                      isActive ? "text-[#D0ADFB]" : "text-neutral-200"
                    }`}
                  >
                    {lang.label}
                  </span>
                  {isActive && (
                    <Icon
                      icon="heroicons:check-circle-20-solid"
                      className="w-[20px] h-[20px] text-[#AD7AEF] ml-auto"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
